import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { apiCall, guestIdParamLocalStorage } from '../../utils/apiCall';
import { getApiUrl } from '../../utils/apiHandler';
import {
  DefaultCustomRatePayload,
  DefaultCustomRateResponse,
  ProductsRateResponse
} from '../_declarations/financialCalculator';
import { setDownPayment, setSelectedItem } from '../slices/appSlice';
import { customRate, productsRate } from './mockForTest';

type Increase = {
  uuid?: string;
};

let controller: AbortController | null = null;

export const postDefaultCustomRate = createAsyncThunk<
  DefaultCustomRateResponse & Increase,
  DefaultCustomRatePayload & Increase,
  any
>(
  'financialCalculator/default-custom-rate',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      if (state.app.testPrupose) {
        return customRate;
      }

      if (controller) {
        controller.abort();
      }

      controller = new AbortController();
      const defaultCustomRate = getApiUrl('defaultCustomRate');
      const { uuid, ...otherPayload } = payload;

      const config = {
        data: otherPayload,
        headers: {
          Authorization: state?.auth?.value || '',
          SessionId: window.localStorage.getItem(guestIdParamLocalStorage) || ''
        },
        method: 'POST',
        signal: controller.signal,
        url: defaultCustomRate
      };

      if (state?.app?.widgetType === 'embedded') {
        const event = new CustomEvent('LeasysEventUpdatedItem', {
          detail: { func: () => apiCall(config), uuid }
        });
        document.dispatchEvent(event);
        return null as any;
      }
      const response: DefaultCustomRateResponse = await apiCall(config);

      return { ...response, uuid };
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error('Error in postDefaultCustomRate:', error);

        return rejectWithValue(error.response);
      }
      throw error;
    }
  }
);

export const postProductsRate = createAsyncThunk<
  ProductsRateResponse,
  DefaultCustomRatePayload,
  any
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>(
  'financialCalculator/products-rate',
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      if (state.app.testPrupose) {
        return productsRate;
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { parameters, ...restPayload } = payload;
      const config = {
        data: restPayload,
        headers: {
          Authorization: state?.auth?.value || '',
          SessionId: window.localStorage.getItem(guestIdParamLocalStorage) || ''
        },
        method: 'POST',
        url: getApiUrl('productsRate')
      };

      if (state?.app?.widgetType === 'embedded') {
        const event = new CustomEvent('LeasysEventUpdatedItem', {
          detail: { func: () => apiCall(config) }
        });
        document.dispatchEvent(event);
        return null as any;
      }
      const response: ProductsRateResponse = await apiCall(config);

      const packageSelection = response.packageSelection.map(
        (singlePackage) => ({
          ...singlePackage,
          uuid: uuidv4()
        })
      );

      if (packageSelection?.length > 0) {
        const item = packageSelection[0];
        dispatch(setSelectedItem(item.uuid));
        const { deposit, depositWithVat } = item.result.teaser.data;
        dispatch(setDownPayment({ deposit, depositWithVat }));
      }

      const newResponse: ProductsRateResponse = {
        ...response,
        packageSelection
      };
      return newResponse;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);
